import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType } from '@/context/types';
import { useAppStore } from '@/stores/useAppStore';

import { QueryKeys } from '../query-keys';

import { getQuantaSignInIdentities } from './auth';
import { proveLeaderboardWallet } from './leaderboardWallet';
import { quantaApi } from './quantaApi';
import { quantaHeadersBelongToWallet, sameQuantaIdentity } from './quantaIdentity';

interface DisplayWalletStatus {
  userId: string;
  revision: number;
  authority: { address: string; publicKey: string; network: 'mainnet' | 'testnet' };
  walletAddress: string | null;
  canUpdate: boolean;
}

export function useLeaderboardWallet(userId?: string) {
  const { wallet, client, walletType, accountRevision } = useDucatClient();
  const headers = useAppStore((state) => state.quantaHeaders);
  const current = useRef({ wallet, walletType, accountRevision, userId });
  current.current = { wallet, walletType, accountRevision, userId };
  const mounted = useRef(true);
  const approving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const queryClient = useQueryClient();
  const enabled = !!userId && !!headers && quantaHeadersBelongToWallet(headers, wallet, accountRevision);
  const paymentSession = enabled && quantaHeadersBelongToWallet(headers, wallet, accountRevision, 'payment');
  const queryKey = ['quanta-leaderboard-wallet', userId, headers?.['x-address'], headers?.['x-user-public-key'], accountRevision];
  const assertCurrent = () => {
    if (
      !enabled ||
      !mounted.current ||
      current.current.wallet !== wallet ||
      current.current.walletType !== walletType ||
      current.current.accountRevision !== accountRevision ||
      current.current.userId !== userId ||
      useAppStore.getState().quantaHeaders !== headers
    ) {
      throw new Error('Wallet changed during leaderboard approval');
    }
  };
  const query = useQuery({
    queryKey,
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      assertCurrent();
      const response = await quantaApi.get<{ data: DisplayWalletStatus }>('/leaderboard-wallet/', { headers });
      assertCurrent();
      if (response.data.data.userId !== userId) throw new Error('Quanta account changed');
      return response.data.data;
    },
  });
  const connected = useQuery({
    queryKey: ['quanta-connected-display', ...queryKey, query.data?.revision],
    enabled: paymentSession && !!query.data && query.data.walletAddress !== headers?.['x-address'],
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: async () => {
      assertCurrent();
      const state = query.data;
      if (!state || !headers || !wallet || !walletType || !paymentSession) throw new Error('Connected wallet is required');
      const live = await getQuantaSignInIdentities(walletType, wallet);
      assertCurrent();
      if (!live || !sameQuantaIdentity(live.payment, { address: headers['x-address'], publicKey: headers['x-user-public-key'] })) {
        throw new Error('Connected wallet changed');
      }
      const response = await quantaApi.post(
        '/leaderboard-wallet/connected',
        { origin: window.location.origin, userId, revision: state.revision },
        { headers },
      );
      assertCurrent();
      if (response.data.data.userId !== userId || response.data.data.walletAddress !== live.payment.address) {
        throw new Error('Quanta account changed');
      }
      queryClient.setQueryData(queryKey, { ...state, walletAddress: live.payment.address });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.quantaRanking] });
      return response.data.data;
    },
  });
  const approval = useMutation({
    mutationFn: async () => {
      if (approving.current) return;
      approving.current = true;
      try {
        assertCurrent();
        const state = query.data;
        if (walletType !== WalletType.Metamask || !state?.canUpdate || state.userId !== userId || !wallet || !headers || !userId) {
          throw new Error('Current Quanta wallet is required');
        }
        const identities = await getQuantaSignInIdentities(walletType, wallet);
        assertCurrent();
        if (!identities?.vault || !sameQuantaIdentity(identities.vault, state.authority)) throw new Error('Quanta wallet changed');
        const vaultIdentity = identities.vault;
        const assertLive = async () => {
          const live = await getQuantaSignInIdentities(walletType, wallet);
          assertCurrent();
          if (!live?.vault || !sameQuantaIdentity(live.vault, vaultIdentity) || !sameQuantaIdentity(live.payment, identities.payment)) {
            throw new Error('Connected wallet changed');
          }
        };
        await proveLeaderboardWallet(
          {
            origin: window.location.origin,
            userId,
            revision: state.revision,
            network: state.authority.network,
            authorityAddress: state.authority.address,
            authorityPublicKey: state.authority.publicKey,
            walletAddress: identities.payment.address,
            walletPublicKey: identities.payment.publicKey,
          },
          headers,
          async (address, message) => {
            await assertLive();
            const response = await client.signMessage({ address, message });
            await assertLive();
            return response?.result?.signature ?? '';
          },
          assertCurrent,
        );
        queryClient.setQueryData(queryKey, { ...state, walletAddress: identities.payment.address });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.quantaRanking] });
      } finally {
        approving.current = false;
      }
    },
  });
  return { ...query, approval, connected, paymentSession, enabled, fundsAddress: wallet?.account.funds.address };
}
